"use client";

import React from "react";
import { Building2, CircleCheck as CheckCircle2, ClipboardCheck, FileCheck2, GraduationCap, HardHat, Landmark, ShieldCheck, Store, Users } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accessEnvironments = [{
  id: "historic",
  title: "Historic town centre & high street",
  description: "Working on pedestrianised cobbles and steep gradients requires out-of-hours delivery, pavement licensing coordination, and protective gantry setups.",
  icon: Landmark,
  badge: "Conservation"
}, {
  id: "period-commercial",
  title: "Period & modern commercial properties",
  description: "Carefully engineered ties protect historic masonry and timber framing, while freestanding cantilever designs accommodate contemporary glass facades.",
  icon: Store,
  badge: "Retail & Offices"
}, {
  id: "conservation",
  title: "Conservation areas & listed buildings",
  description: "Non-intrusive clamping systems, protective membrane wrapping, and careful weight distribution ensure compliance with local conservation officers.",
  icon: Building2,
  badge: "Heritage"
}, {
  id: "university",
  title: "University & institutional sites",
  description: "Phased builds adjacent to active lecture halls and student accommodation with segregated pedestrian walkways, debris netting, and strict acoustic controls.",
  icon: GraduationCap,
  badge: "Higher Education"
}];
const safetySteps = [{
  step: "01",
  title: "Detailed site assessment",
  description: "Every project begins with an on-site survey to inspect ground bearing capacity, public thoroughfares, overhead lines, and building access constraints.",
  icon: HardHat
}, {
  step: "02",
  title: "Engineered safe erection",
  description: "Our CISRS-carded scaffolders erect structures according to strict TG20 guidance and project-specific risk assessments (RAMS).",
  icon: Users
}, {
  step: "03",
  title: "Formal handover inspection",
  description: "Before your trades step onto the platform, a qualified supervisor inspects the structure and signs a formal Handover Certificate with a clear Scafftag.",
  icon: ClipboardCheck
}, {
  step: "04",
  title: "Statutory 7-day monitoring",
  description: "We complete scheduled 7-day safety inspections and prompt weather-event reviews, logging all records directly for your site health and safety file.",
  icon: FileCheck2
}];
const accreditations = [{
  name: "NASC",
  description: "National Access & Scaffolding Confederation member",
  detail: "Audited safety standards, training, and operational compliance."
}, {
  name: "CHAS",
  description: "Contractors Health and Safety Assessment Scheme",
  detail: "Verified health, safety, and risk management management processes."
}];
export default function GuildfordAccess() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="guildfordaccessandsafety" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4">
            Guildford & Surrey Hills
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Safe scaffolding across Guildford's varied terrain
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            From the steep cobbled High Street to modern commercial business
            parks and active university campuses, every scaffold is planned for
            clean access, minimal disruption, and full safety compliance.
          </p>
        </div>

        {/* Access Scenarios Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {accessEnvironments.map((env, i) => {
          const IconComponent = env.icon;
          return <motion.div key={env.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="inline-flex size-10 items-center justify-center rounded-lg bg-muted text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {env.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {env.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {env.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <Separator className="my-16 md:my-20" />

        {/* Safety & Handover Process Section */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Safety Narrative & Accreditations */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-4" />
                <span>Safety-led methodology</span>
              </div>
              <h3 className="mt-2 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Rigorous site control from survey to sign-off
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                Scaffolding in populated urban centres and sensitive heritage
                zones demands total operational discipline. BMF Scaffolding Ltd
                combines hands-on experience with certified processes to protect
                workers, the public, and existing buildings.
              </p>
            </div>

            {/* Accreditations Block */}
            <div className="rounded-xl border border-border bg-muted/40 p-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Verified accreditations
              </h4>
              <div className="mt-4 space-y-4">
                {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="flex gap-3">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {acc.name} —{" "}
                        <span className="font-normal text-muted-foreground">
                          {acc.description}
                        </span>
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {acc.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Quick Action Button */}
            <div className="pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage">
                  Request a Guildford site visit
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: 4-Step Safety Flow */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {safetySteps.map((step, i) => {
              const StepIcon = step.icon;
              return <div key={step.step} data-index={i} className="relative flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-bold text-primary">
                          STEP {step.step}
                        </span>
                        <div className="inline-flex size-8 items-center justify-center rounded-md bg-muted text-foreground">
                          <StepIcon className="size-4" />
                        </div>
                      </div>
                      <h4 className="mt-4 text-base font-semibold text-card-foreground">
                        {step.title}
                      </h4>
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground md:text-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}